/**
 * EdgeStream Hub Adapter with Separate Pipelines
 *
 * Demonstrates advanced pipeline architecture:
 * - Separate pipelines for incoming and outgoing messages
 * - Custom hooks for different message directions
 * - Direction-specific validation and processing
 * - Custom subscribers for extended functionality
 */

import * as signalR from '@microsoft/signalr';
import type { IEnvelope } from 'edge-stream-js';
import {
  getLoggingService,
  IncomingPipeline,
  PipelineContext,
  PublishToSubscribersHook,
  DependencyContainer,
  SIGNALR_EVENTS,
  createGuid,
} from 'edge-stream-js';
import type {
  ISignalRConnection,
  IHandlerRegistry,
  IMessageTransformer,
  ISubscriberDistributor,
  ILoggingStrategy,
  IReconnectionStrategy,
} from 'edge-stream-js';

// Import custom subscribers (used as fallback defaults in constructor)
import { CustomIncomingSubscriber } from '../subscribers/incoming';
import { CustomOutgoingSubscriber } from '../subscribers/outgoing';

// Import pipeline system
import { getPipelineRegistry } from '../pipelines';
import type { PipelineMetadata } from '../pipelines';

// Import group naming utility for consistency with C# HubGroup format
import { buildGroupFullName } from '../utils/groupNaming';

export interface EdgeStreamHubAdapterConfig {
  hubUrl: string;
  serverId: string;
  enableLogging?: boolean;
  /** ID of a registered incoming PipelineDefinition. Defaults to 'edgestream.default-incoming-v1'. */
  incomingPipelineId?: string;
  /** ID of a registered outgoing PipelineDefinition. Defaults to 'edgestream.default-outgoing-v1'. */
  outgoingPipelineId?: string;
}

/**
 * Optional injectable services for dependency injection
 * If not provided, services are created via DependencyContainer
 * This enables testing and custom implementations
 */
export interface EdgeStreamHubAdapterServices {
  connection?: ISignalRConnection;
  handlers?: IHandlerRegistry;
  transformer?: IMessageTransformer;
  distributor?: ISubscriberDistributor;
  logger?: ILoggingStrategy;
  reconnect?: IReconnectionStrategy;
}

export type MessageCallback = (envelope: IEnvelope) => void;

/**
 * Adapter connecting SignalR hub to EdgeStream pipeline
 * Features separate pipelines for incoming and outgoing messages
 * Phase 2: Refactored to use enterprise SRP services via dependency injection
 */
export class EdgeStreamHubAdapter {
  private hubUrl: string;
  private serverId: string;
  private connection: signalR.HubConnection | null = null;
  private isConnected = false;
  private isInGroup = false;
  private messageCallbacks: Map<string, MessageCallback[]> = new Map();
  private loggingService = getLoggingService();
  private enableLogging: boolean;

  // Enterprise services (injected or created via DependencyContainer)
  private handlerRegistry: IHandlerRegistry;

  // Separate pipelines for each direction
  private incomingPipeline!: IncomingPipeline;
  private outgoingPipeline!: IncomingPipeline;

  // Custom subscribers for extended functionality
  private incomingSubscriber!: CustomIncomingSubscriber;
  private outgoingSubscriber!: CustomOutgoingSubscriber;

  // Pipeline metadata for active pipelines
  private activeIncomingPipelineMetadata!: PipelineMetadata;
  private activeOutgoingPipelineMetadata!: PipelineMetadata;

  // Hook instances used for logging
  private incomingHookInstances: any[] = [];
  private outgoingHookInstances: any[] = [];

  constructor(config: EdgeStreamHubAdapterConfig, services?: EdgeStreamHubAdapterServices) {
    this.hubUrl = config.hubUrl;
    this.serverId = config.serverId;
    this.enableLogging = config.enableLogging ?? true;

    // Resolve enterprise services from DI or use provided ones
    // Phase 2: Dependency injection for enterprise services
    const container = new DependencyContainer({
      signalrSettings: {
        connection: {
          url: config.hubUrl,
          timeout: 30000,
          withCredentials: false,
          skipNegotiation: false,
          transports: ['WebSockets', 'LongPolling'],
        },
      },
    });

    this.handlerRegistry = services?.handlers ?? container.getHandlerRegistry();

    // Resolve pipeline definitions from registry
    const registry = getPipelineRegistry();
    const incomingDef =
      registry.get(config.incomingPipelineId ?? 'edgestream.default-incoming-v1') ??
      registry.get('edgestream.default-incoming-v1')!;

    const outgoingDef =
      registry.get(config.outgoingPipelineId ?? 'edgestream.default-outgoing-v1') ??
      registry.get('edgestream.default-outgoing-v1')!;

    // Store active metadata
    this.activeIncomingPipelineMetadata = incomingDef.metadata;
    this.activeOutgoingPipelineMetadata = outgoingDef.metadata;

    // Build pipelines from definitions
    const incomingHooks = incomingDef.createHooks();
    const outgoingHooks = outgoingDef.createHooks();

    // Store hook instances (all except PublishToSubscribersHook) for logging
    this.incomingHookInstances = incomingHooks.filter(
      (h) => !(h instanceof PublishToSubscribersHook)
    );
    this.outgoingHookInstances = outgoingHooks.filter(
      (h) => !(h instanceof PublishToSubscribersHook)
    );

    // Create pipelines
    this.incomingPipeline = new IncomingPipeline(incomingHooks);
    this.outgoingPipeline = new IncomingPipeline(outgoingHooks);

    // Build subscribers from definitions
    const incomingSubscribers = incomingDef.createSubscribers?.() ?? [];
    const outgoingSubscribers = outgoingDef.createSubscribers?.() ?? [];

    this.incomingSubscriber =
      (incomingSubscribers[0] as CustomIncomingSubscriber) ??
      new CustomIncomingSubscriber();
    this.outgoingSubscriber =
      (outgoingSubscribers[0] as CustomOutgoingSubscriber) ??
      new CustomOutgoingSubscriber();

    console.log(
      `[EdgeStreamHubAdapter] 📥 Pipeline loaded: incoming="${incomingDef.metadata.name}", outgoing="${outgoingDef.metadata.name}"`
    );
  }

  /**
   * Get metadata for the currently active incoming pipeline
   */
  getIncomingPipelineMetadata(): PipelineMetadata {
    return this.activeIncomingPipelineMetadata;
  }

  /**
   * Get metadata for the currently active outgoing pipeline
   */
  getOutgoingPipelineMetadata(): PipelineMetadata {
    return this.activeOutgoingPipelineMetadata;
  }

  /**
   * Connect to SignalR hub
   */
  async connect(): Promise<void> {
    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
          timeout: 30000,
          withCredentials: false,
        })
        .withAutomaticReconnect([1000, 2000, 5000, 10000, 30000, 60000])
        .withHubProtocol(new signalR.JsonHubProtocol())
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Register SignalR event handlers
      this.registerSignalRHandlers();

      await this.connection.start();
      this.isConnected = true;

      console.log('[EdgeStreamHubAdapter] ✅ Connected to hub');
    } catch (error) {
      console.error('[EdgeStreamHubAdapter] ❌ Connection failed:', error);
      throw error;
    }
  }

  /**
   * Disconnect from hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.isConnected = false;
      this.isInGroup = false;
      console.log('[EdgeStreamHubAdapter] Disconnected from hub');
    }
  }

  /**
   * Join a server group (for monitoring all messages)
   */
  async joinServer(): Promise<void> {
    if (!this.connection) {
      throw new Error('Not connected to hub');
    }

    try {
      await this.connection.invoke('JoinServerMonitoring', this.serverId);
      console.log(`[EdgeStreamHubAdapter] ✅ Joined server: ${this.serverId}`);
    } catch (error) {
      console.error('[EdgeStreamHubAdapter] ❌ Failed to join server:', error);
      throw error;
    }
  }

  /**
   * Join a group (chat, task, etc.)
   * Uses consistent group naming: tenant@{TenantID}:server@{ServerID}:group@{GroupName}
   */
  async joinGroup(groupName: string): Promise<void> {
    if (!this.connection) {
      throw new Error('Not connected to hub');
    }

    try {
      const groupFullName = buildGroupFullName(this.serverId, groupName);
      await this.connection.invoke('JoinGroup', this.serverId, groupName);
      this.isInGroup = true;
      console.log(`[EdgeStreamHubAdapter] ✅ Joined group: ${groupFullName}`);
    } catch (error) {
      console.error('[EdgeStreamHubAdapter] ❌ Failed to join group:', error);
      throw error;
    }
  }

  /**
   * Leave a group
   * Uses consistent group naming: tenant@{TenantID}:server@{ServerID}:group@{GroupName}
   */
  async leaveGroup(groupName: string): Promise<void> {
    if (!this.connection) {
      throw new Error('Not connected to hub');
    }

    try {
      const groupFullName = buildGroupFullName(this.serverId, groupName);
      await this.connection.invoke('LeaveGroup', this.serverId, groupName);
      this.isInGroup = false;
      console.log(`[EdgeStreamHubAdapter] ✅ Left group: ${groupFullName}`);
    } catch (error) {
      console.error('[EdgeStreamHubAdapter] ❌ Failed to leave group:', error);
      throw error;
    }
  }

  /**
   * Subscribe to messages with topic pattern
   * Messages will be processed through EdgeStream pipeline
   */
  subscribe(topic: string, callback: MessageCallback): () => void {
    if (!this.messageCallbacks.has(topic)) {
      this.messageCallbacks.set(topic, []);
    }

    this.messageCallbacks.get(topic)!.push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.messageCallbacks.get(topic);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Send message to group via pipeline
   */
  async sendMessage(groupName: string, author: string, text: string): Promise<boolean> {
    if (!this.connection) {
      console.error('[EdgeStreamHubAdapter] Not connected');
      return false;
    }

    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const topic = `group:${this.serverId}:${groupName}`;
    const messageData = { author, text, messageId, timestamp: new Date().toISOString() };

    try {
      // Log outgoing message
      if (this.enableLogging) {
        const log = this.loggingService.createMessageLog(messageId, this.serverId, topic, 'chat.message');
        log.protocol = 'json';
        log.direction = 'outgoing';
        (log as any).rawMessage = JSON.stringify(messageData);

        console.log(`[EdgeStreamHubAdapter] 📤 Logging outgoing message: ${messageId}`);
      }

      // Send via SignalR
      await this.connection.invoke('SendChatMessage', this.serverId, groupName, author, text);

      // Process outgoing message through pipeline and subscribers (for audit trail and real-time updates)
      if (this.enableLogging) {
        const pipelineStartTime = Date.now();

        try {
          // Create envelope for outgoing message
          const envelope = {
            meta: {
              id: messageId,
              serverId: this.serverId,
              transport: 'signalr',
              protocol: 'json',
              receivedAt: new Date(),
              topic: topic,
              type: 'chat.message',
            },
            direction: 'outgoing',
            raw: JSON.stringify(messageData),
            body: {
              data: {
                metadata: {
                  source: 'signalr-hub',
                  receivedAt: new Date().toISOString(),
                  messageType: 'chat.message',
                },
                data: messageData,
              },
            },
            headers: {
              'content-type': 'application/json',
              'x-transport': 'signalr',
              'x-event-type': 'chat.message',
            },
          } as IEnvelope;

          // Execute outgoing pipeline
          const context = new PipelineContext(envelope, this.serverId);
          await this.outgoingPipeline.execute(context);
          const pipelineDuration = Date.now() - pipelineStartTime;

          console.log(`[EdgeStreamHubAdapter] 🚀 Outgoing pipeline completed: ${messageId} (${pipelineDuration}ms)`);

          // Log hook execution for outgoing pipeline with metadata
          for (const hook of this.outgoingHookInstances) {
            const hookMetadata = (hook as any).metadata;
            (this.loggingService.logHookExecution as any)(messageId, {
              hookName: hook.name || hook.constructor.name,
              status: 'success',
              priority: (hook as any).priority || 0,
              timestamp: new Date().toISOString(),
              description: hookMetadata?.description,
              version: hookMetadata?.version,
              author: hookMetadata?.author,
              tags: hookMetadata?.tags,
              modifiesData: hookMetadata?.modifiesData,
              impactLevel: hookMetadata?.impactLevel,
            });
          }

          // Deliver to subscribers
          this.deliverToSubscribers(envelope, topic);

          // Mark as completed
          this.loggingService.completeMessageLog(messageId, 'completed');

          console.log(`[EdgeStreamHubAdapter] ✅ Outgoing message processed: ${messageId}`);
        } catch (pipelineError) {
          console.warn(`[EdgeStreamHubAdapter] ⚠️ Outgoing pipeline error (continuing): ${pipelineError}`);

          // Log error but mark as sent since the message was actually sent via SignalR
          this.loggingService.logHookExecution(messageId, {
            hookName: 'PipelineExecution',
            status: 'error',
            priority: 0,
            timestamp: new Date().toISOString(),
          });

          this.loggingService.completeMessageLog(messageId, 'completed');
        }
      }

      return true;
    } catch (error) {
      console.error('[EdgeStreamHubAdapter] ❌ Failed to send message:', error);
      return false;
    }
  }

  /**
   * Register SignalR event handlers
   * Phase 2: Register all 12 events using SIGNALR_EVENTS constant
   * Routes events by type and handles critical confirmations
   */
  private registerSignalRHandlers(): void {
    if (!this.connection) return;

    // Register all 12 SignalR events from constant
    for (const eventConfig of SIGNALR_EVENTS) {
      const eventName = eventConfig.name;

      this.connection.on(eventName, (rawMessage: any) => {
        // Route by event type
        switch (eventConfig.type) {
          case 'confirmation':
            // Handle critical confirmations that update state
            this.handleConfirmation(eventName, rawMessage);
            break;

          case 'error':
            console.error(`[EdgeStreamHubAdapter] Hub error (${eventName}):`, rawMessage);
            break;

          case 'message':
          case 'group-broadcast':
          case 'event':
            // Messages and broadcasts processed through pipeline
            // Derive topic from event type
            const topic =
              eventConfig.type === 'group-broadcast'
                ? `group.${eventName.toLowerCase()}`
                : eventName.toLowerCase();
            this.handleSignalRMessage(topic, rawMessage);
            break;

          case 'status':
            // Status updates logged but not processed
            console.log(`[EdgeStreamHubAdapter] Status (${eventName}):`, rawMessage);
            break;
        }
      });

      // Register handler in registry for validation
      this.handlerRegistry.register(eventName, (_rawMessage: any) => {
        // Handler is already registered above via connection.on()
        // This registers it for diagnostics and validation
      });
    }

    // Validate all required handlers registered
    // Critical handlers: ReceiveGroupJoinedConfirm, ReceiveGroupLeftConfirm, ReceiveChatMessageConfirm
    const criticalHandlers = ['ReceiveGroupJoinedConfirm', 'ReceiveGroupLeftConfirm', 'ReceiveChatMessageConfirm'];
    const missing = this.handlerRegistry.validateRequired(criticalHandlers);

    if (missing.length > 0) {
      console.warn('[EdgeStreamHubAdapter] Missing critical handlers:', missing);
    } else {
      console.log('[EdgeStreamHubAdapter] ✅ All 12 SignalR handlers registered');
    }
  }

  /**
   * Handle critical confirmation messages
   * Updates state and emits confirmation to subscribers
   */
  private handleConfirmation(eventName: string, _rawMessage: any): void {
    const messageId = _rawMessage?.messageId || `confirm-${Date.now()}`;

    switch (eventName) {
      case 'ReceiveGroupJoinedConfirm':
        // Server confirmed we joined the group
        this.isInGroup = true;
        console.log('[EdgeStreamHubAdapter] ✅ Group join confirmed');

        // Emit confirmation to subscribers
        this.emitConfirmationToSubscribers(eventName, _rawMessage, messageId);
        break;

      case 'ReceiveGroupLeftConfirm':
        // Server confirmed we left the group
        this.isInGroup = false;
        console.log('[EdgeStreamHubAdapter] ✅ Group leave confirmed');

        // Emit confirmation to subscribers
        this.emitConfirmationToSubscribers(eventName, _rawMessage, messageId);
        break;

      case 'ReceiveChatMessageConfirm':
        // Server confirmed message was delivered
        console.log('[EdgeStreamHubAdapter] ✅ Chat message confirmed');

        // Emit confirmation to subscribers (UI can show ✓ check mark)
        this.emitConfirmationToSubscribers(eventName, _rawMessage, messageId);
        break;
    }
  }

  /**
   * Emit confirmation envelope to subscribers
   */
  private emitConfirmationToSubscribers(eventName: string, rawMessage: any, messageId: string): void {
    const topic = `confirm:${eventName}`;
    const envelope: IEnvelope = {
      meta: {
        id: messageId,
        serverId: this.serverId,
        transport: 'signalr',
        protocol: 'json',
        receivedAt: new Date(),
        topic: topic,
        type: eventName,
      },
      direction: 'incoming',
      raw: JSON.stringify(rawMessage),
      body: {
        data: {
          metadata: {
            source: 'signalr-hub',
            receivedAt: new Date().toISOString(),
            messageType: eventName,
            isConfirmation: true,
          },
          data: rawMessage,
        },
      },
      headers: {
        'content-type': 'application/json',
        'x-transport': 'signalr',
        'x-event-type': eventName,
      },
    };

    // Deliver to subscribers
    this.deliverToSubscribers(envelope, topic);
  }

  /**
   * Handle incoming SignalR message
   * Convert to IEnvelope, execute pipeline, and deliver to subscribers
   */
  private async handleSignalRMessage(messageType: string, rawMessage: any): Promise<void> {
    try {
      const messageId = rawMessage.messageId || createGuid();
      const topic = rawMessage.topic || `${messageType}`;
      const serverId = rawMessage.serverId || this.serverId;

      // Log message received
      if (this.enableLogging) {
        const log = this.loggingService.createMessageLog(messageId, serverId, topic, messageType);
        log.protocol = 'json';
        log.direction = 'incoming';
      }

      // Create IEnvelope from raw SignalR message
      const envelope = this.createEnvelopeFromSignalR(rawMessage, messageType, serverId, topic, messageId);

      const pipelineStartTime = Date.now();
      console.log(`[EdgeStreamHubAdapter] 📥 Executing incoming pipeline for message: ${messageId}`);
      try {
        const context = new PipelineContext(envelope, serverId);
        await this.incomingPipeline.execute(context);
        const pipelineDuration = Date.now() - pipelineStartTime;
        console.log(`[EdgeStreamHubAdapter] ✅ Incoming pipeline completed: ${messageId} (${pipelineDuration}ms)`);

        // Log hook execution details with metadata
        if (this.enableLogging) {
          for (const hook of this.incomingHookInstances) {
            const hookMetadata = (hook as any).metadata;
            (this.loggingService.logHookExecution as any)(messageId, {
              hookName: hook.name || hook.constructor.name,
              status: 'success',
              priority: (hook as any).priority || 0,
              timestamp: new Date().toISOString(),
              description: hookMetadata?.description,
              version: hookMetadata?.version,
              author: hookMetadata?.author,
              tags: hookMetadata?.tags,
              modifiesData: hookMetadata?.modifiesData,
              impactLevel: hookMetadata?.impactLevel,
            });
          }
        }
      } catch (pipelineError) {
        console.warn(`[EdgeStreamHubAdapter] ⚠️ Pipeline error (continuing): ${pipelineError}`);

        // Log hook error
        if (this.enableLogging) {
          this.loggingService.logHookExecution(messageId, {
            hookName: 'PipelineExecution',
            status: 'error',
            priority: 0,
            timestamp: new Date().toISOString(),
          });
        }
      }

      // Deliver to matching subscribers
      this.deliverToSubscribers(envelope, topic);

      // Mark message as completed
      if (this.enableLogging) {
        this.loggingService.completeMessageLog(messageId, 'completed');
      }
    } catch (error) {
      console.error('[EdgeStreamHubAdapter] Error handling message:', error);
    }
  }

  /**
   * Create IEnvelope from raw SignalR message
   */
  private createEnvelopeFromSignalR(
    rawMessage: any,
    messageType: string,
    serverId: string,
    topic: string,
    messageId: string,
  ): IEnvelope {
    const now = new Date();

    return {
      meta: {
        id: messageId,
        serverId: serverId,
        transport: 'signalr',
        protocol: 'json', // Will be normalized to 'cloudevents' by pipeline
        receivedAt: now,
        topic: topic,
        type: messageType,
      },
      direction: 'incoming',
      raw: JSON.stringify(rawMessage),
      body: {
        // Structured as BizFirstAI standard
        data: {
          metadata: {
            source: 'signalr-hub',
            receivedAt: now.toISOString(),
            messageType: messageType,
          },
          data: rawMessage, // Original SignalR message payload
        },
      },
      headers: {
        'content-type': 'application/json',
        'x-transport': 'signalr',
        'x-event-type': messageType,
      },
    };
  }

  /**
   * Deliver envelope to matching subscribers
   * Includes default callbacks + custom subscribers for extended processing
   */
  private deliverToSubscribers(envelope: IEnvelope, topic: string): void {
    const messageId = envelope.meta.id as string;
    const direction = (envelope as any).direction || 'incoming';

    // Get registered message callbacks
    const callbacks = this.messageCallbacks.get(topic) || [];

    // Also check wildcard subscribers
    const wildcardCallbacks = Array.from(this.messageCallbacks.entries())
      .filter(([key]) => this.topicMatches(key, topic))
      .flatMap(([_, cbs]) => cbs);

    const allCallbacks = [...callbacks, ...wildcardCallbacks];

    // Deliver to registered callbacks with unique indexing
    for (let i = 0; i < allCallbacks.length; i++) {
      const callback = allCallbacks[i];
      const callbackIndex = i + 1; // 1-indexed for display

      try {
        const startTime = Date.now();
        callback(envelope);
        const duration = Date.now() - startTime;

        // Log subscriber delivery
        if (this.enableLogging) {
          const subscriberId = callback.name || `MessageCallback#${callbackIndex}(${topic})`;
          this.loggingService.logSubscriberDelivery(messageId, {
            subscriberId,
            topic: topic,
            status: 'delivered',
            processingTime: duration > 0 ? duration : 1, // Minimum 1ms for visibility
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('[EdgeStreamHubAdapter] Subscriber error:', error);
        if (this.enableLogging) {
          const subscriberId = callback.name || `MessageCallback#${callbackIndex}(${topic})`;
          this.loggingService.logSubscriberDelivery(messageId, {
            subscriberId,
            topic: topic,
            status: 'error',
            error: {
              message: error instanceof Error ? error.message : String(error),
            },
            timestamp: new Date().toISOString(),
          });
        }
      }
    }

    // Deliver to custom subscribers based on direction
    try {
      if (direction === 'incoming') {
        const startTime = Date.now();
        const customCallback = this.incomingSubscriber.subscribe((_env: IEnvelope) => {
          // Custom incoming processing happens in the subscriber
        });
        customCallback(envelope);
        const duration = Date.now() - startTime;

        if (this.enableLogging) {
          const subMetadata = (this.incomingSubscriber as any).metadata;
          (this.loggingService.logSubscriberDelivery as any)(messageId, {
            subscriberId: this.incomingSubscriber.name || 'CustomIncomingSubscriber',
            topic: topic,
            status: 'delivered',
            processingTime: duration > 0 ? duration : 2,
            timestamp: new Date().toISOString(),
            description: subMetadata?.description,
            version: subMetadata?.version,
            author: subMetadata?.author,
            tags: subMetadata?.tags,
            modifiesData: subMetadata?.modifiesData,
            impactLevel: subMetadata?.impactLevel,
            isBlocking: subMetadata?.isBlocking,
          });
        }
      } else if (direction === 'outgoing') {
        const startTime = Date.now();
        const customCallback = this.outgoingSubscriber.subscribe((_env: IEnvelope) => {
          // Custom outgoing processing happens in the subscriber
        });
        customCallback(envelope);
        const duration = Date.now() - startTime;

        if (this.enableLogging) {
          const subMetadata = (this.outgoingSubscriber as any).metadata;
          (this.loggingService.logSubscriberDelivery as any)(messageId, {
            subscriberId: this.outgoingSubscriber.name || 'CustomOutgoingSubscriber',
            topic: topic,
            status: 'delivered',
            processingTime: duration > 0 ? duration : 2,
            timestamp: new Date().toISOString(),
            description: subMetadata?.description,
            version: subMetadata?.version,
            author: subMetadata?.author,
            tags: subMetadata?.tags,
            modifiesData: subMetadata?.modifiesData,
            impactLevel: subMetadata?.impactLevel,
            isBlocking: subMetadata?.isBlocking,
          });
        }
      }
    } catch (error) {
      console.warn('[EdgeStreamHubAdapter] Custom subscriber error:', error);
    }
  }

  /**
   * Check if topic matches pattern (supports wildcards)
   */
  private topicMatches(pattern: string, actual: string): boolean {
    if (pattern === '*') return true;
    if (pattern === actual) return true;

    const regex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`);
    return regex.test(actual);
  }

  /**
   * Get connection status
   */
  getIsConnected(): boolean {
    return this.isConnected && this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get group status
   */
  getIsInGroup(): boolean {
    return this.isInGroup;
  }

  /**
   * Enable/disable logging
   */
  setLoggingEnabled(enabled: boolean): void {
    this.enableLogging = enabled;
  }
}
